import React from "react";
import { Link } from "react-router-dom";
import { Target, Eye, Heart, ShieldCheck, ArrowRight } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { usePageMeta } from "@/lib/seo";

const VALUES = [
  { icon: ShieldCheck, t: "Integrity", d: "Transparent pricing, honest advice and receipted payments — always." },
  { icon: Heart, t: "Genuine Care", d: "We treat every traveller's trip like our own, from enquiry to safe return." },
  { icon: Target, t: "Reliability", d: "We do what we say, when we say it. Clear communication at every stage." },
  { icon: Eye, t: "Excellence", d: "International service standards across all three of our registered offices." },
];

export default function About() {
  usePageMeta("About Us", "Learn about Makchester Tours & Travel — an international travel company registered in the UAE, Zimbabwe and South Africa, delivering trusted travel solutions worldwide.");
  return (
    <div className="bg-white">
      <section className="relative py-28 bg-[#0A2E4D] overflow-hidden">
        <img src="https://images.pexels.com/photos/1268855/pexels-photo-1268855.jpeg?auto=compress&cs=tinysrgb&w=1920" alt="Travel" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="mk-gold-line mb-6 inline-block" />
          <p className="text-xs uppercase tracking-[0.25em] text-[#E3BA45] font-bold mb-3">About Us</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white">Your Gateway to the World</h1>
          <p className="text-slate-300 mt-5 max-w-2xl mx-auto leading-relaxed">Makchester Tours &amp; Travel is an international travel company providing flights, visas, hotels, holiday packages, tours, airport transfers, travel insurance and corporate travel services.</p>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <SectionHeading center={false} eyebrow="Our Story" title="Built to make world-class travel accessible" />
            <div className="space-y-5 text-[#4B5563] leading-relaxed -mt-8">
              <p>Makchester Tours &amp; Travel was founded on a simple belief: travellers from Africa and the Middle East deserve the same seamless, premium travel experience as anyone in the world.</p>
              <p>Today we are registered in the <strong className="text-[#0A2E4D]">United Arab Emirates</strong>, <strong className="text-[#0A2E4D]">Zimbabwe</strong> and <strong className="text-[#0A2E4D]">South Africa</strong>, proudly serving travellers worldwide — individuals, families, honeymooners, groups, students and businesses.</p>
              <p>Our mission is to make travel <em>simple, affordable and unforgettable</em>. Our vision is to become one of Africa's leading international travel brands, known for reliability, fair pricing and genuine customer care.</p>
            </div>
            <Link to="/contact" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#0A2E4D] border-b-2 border-[#D4A62A] pb-1 hover:gap-3 transition-all">Talk to our team <ArrowRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=800&auto=format&fit=crop" alt="Dubai" loading="lazy" className="rounded-2xl h-64 w-full object-cover" />
            <img src="https://images.unsplash.com/photo-1678714001094-ba90abd57fec?q=80&w=800&auto=format&fit=crop" alt="Victoria Falls" loading="lazy" className="rounded-2xl h-64 w-full object-cover mt-8" />
            <img src="https://images.unsplash.com/photo-1606799955515-85468ee78c26?q=80&w=800&auto=format&fit=crop" alt="Cape Town" loading="lazy" className="rounded-2xl h-64 w-full object-cover" />
            <img src="https://images.unsplash.com/photo-1514282401047-d79a71a590e8?q=80&w=800&auto=format&fit=crop" alt="Maldives" loading="lazy" className="rounded-2xl h-64 w-full object-cover mt-8" />
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Mission, Vision & Values" title="What we stand for" />
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12">
            <div className="mk-card p-9">
              <Target size={28} className="text-[#D4A62A]" strokeWidth={1.5} />
              <h3 className="font-serif-display text-xl text-[#0A2E4D] mt-4">Our Mission</h3>
              <p className="text-sm text-[#4B5563] mt-2.5 leading-relaxed">To make travel simple, affordable and unforgettable for every client — with reliable visa assistance, honest pricing and world-class support.</p>
            </div>
            <div className="mk-card p-9">
              <Eye size={28} className="text-[#D4A62A]" strokeWidth={1.5} />
              <h3 className="font-serif-display text-xl text-[#0A2E4D] mt-4">Our Vision</h3>
              <p className="text-sm text-[#4B5563] mt-2.5 leading-relaxed">To become one of Africa's leading international travel brands — trusted from Harare to Dubai to Johannesburg and beyond.</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v) => (
              <div key={v.t} className="mk-card p-8 text-center">
                <v.icon size={26} className="text-[#D4A62A] mx-auto" strokeWidth={1.5} />
                <h4 className="font-serif-display text-lg text-[#0A2E4D] mt-4">{v.t}</h4>
                <p className="text-[13px] text-[#4B5563] mt-2 leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionHeading eyebrow="Registered & Trusted" title="Three countries. One standard." />
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { c: "United Arab Emirates", d: "Sharjah Media City, Al Messaned, Al Bataeh, Sharjah, UAE — our Gulf hub for visas and Dubai holidays." },
              { c: "Zimbabwe", d: "Head office — Office 207, Gelfand House, Cnr 1st & Speke Ave, Harare, Zimbabwe." },
              { c: "South Africa", d: "Office 22, Startek Building Block B, 5 Eglin Road, Sunninghill, Sandton, Johannesburg." },
            ].map((x) => (
              <div key={x.c} className="mk-card p-8">
                <ShieldCheck size={26} className="text-[#D4A62A] mx-auto" />
                <h4 className="font-serif-display text-lg text-[#0A2E4D] mt-4">{x.c}</h4>
                <p className="text-[13px] text-[#4B5563] mt-2 leading-relaxed">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
